import React, {useEffect, useState} from 'react';
import {CustomModal} from "../../../../../components/UI/AntCustom/CustomModal";
import {CustomButton} from "../../../../../components/UI/AntCustom/CustomButton";
import {useIntl, defineMessages} from "react-intl";
import {useAppDispatch, useAppSelector} from "../../../../../store/store";
import {
  createSetupIntent,
  clearCreateSetupIntent,
  attachPaymentMethod,
  clearAttachPaymentMethod,
} from "../../../../../store/reducers/tariffReducer/tariffReducer";
import styled from "styled-components";
import {Alert, Form, message} from "antd";
import {loadStripe} from '@stripe/stripe-js';
import {Elements, CardElement, useStripe, useElements} from '@stripe/react-stripe-js';

const PaymentMethodModalMessages = defineMessages({
  title: {
    id: "paymentMethodModal.title",
    defaultMessage: "Add Payment Method",
  },
  description: {
    id: "paymentMethodModal.description",
    defaultMessage: "Please enter your card details to continue",
  },
  cardNumber: {
    id: "paymentMethodModal.cardNumber",
    defaultMessage: "Card Number",
  },
  cardExpiry: {
    id: "paymentMethodModal.cardExpiry",
    defaultMessage: "Expiry Date",
  },
  cardCvc: {
    id: "paymentMethodModal.cardCvc",
    defaultMessage: "CVC",
  },
  submit: {
    id: "paymentMethodModal.submit",
    defaultMessage: "Add Card",
  },
  cancel: {
    id: "paymentMethodModal.cancel",
    defaultMessage: "Cancel",
  },
  success: {
    id: "paymentMethodModal.success",
    defaultMessage: "Payment method added successfully",
  },
  error: {
    id: "paymentMethodModal.error",
    defaultMessage: "Failed to add payment method",
  },
});

const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: 24px;
`;

const FormWrapper = styled.div`
  .StripeElement {
    padding: 10px 12px;
    border: 1px solid #d9d9d9;
    border-radius: 4px;
    background: white;
  }
  
  .StripeElement--focus {
    border-color: #40a9ff;
    box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2);
  }
  
  .StripeElement--invalid {
    border-color: #ff4d4f;
  }
`;

interface IPaymentMethodModal {
  isVisible: boolean;
  setIsVisible: (visible: boolean) => void;
  onSuccess?: () => void;
}

// Payment method form component with Stripe Elements
const PaymentMethodForm: React.FC<{
  clientSecret: string;
  onSubmit: (paymentMethodId: string) => void;
  onCancel: () => void;
  attachLoading: boolean;
  attachError: string;
}> = ({clientSecret, onSubmit, onCancel, attachLoading, attachError}) => {
  const intl = useIntl();
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  useEffect(() => {
    if (!attachLoading) setLoading(false);
  }, [attachLoading]);

  const error = attachError || localError;

  const handleSubmit = async () => {
    if (!stripe || !elements) {
      setLocalError('Stripe is not initialized');
      return;
    }

    const cardElement = elements.getElement(CardElement);
    if (!cardElement) {
      setLocalError('Card element not found');
      return;
    }

    setLoading(true);
    setLocalError(null);

    try {
      if (!clientSecret) {
        setLocalError('Setup Intent client secret is missing');
        setLoading(false);
        return;
      }

      const {error: stripeError, setupIntent} = await stripe.confirmCardSetup(clientSecret, {
        payment_method: {
          card: cardElement,
          billing_details: {},
        },
      });

      if (stripeError) {
        setLocalError(stripeError.message || 'Failed to confirm card setup');
        setLoading(false);
        return;
      }

      if (!setupIntent) {
        setLocalError('Setup Intent not returned from Stripe');
        setLoading(false);
        return;
      }

      const paymentMethodId = typeof setupIntent.payment_method === 'string'
        ? setupIntent.payment_method
        : setupIntent.payment_method?.id;

      if (paymentMethodId) {
        onSubmit(paymentMethodId);
      } else {
        setLocalError('Payment method not found in setup intent');
        setLoading(false);
      }
    } catch (err: any) {
      setLocalError(err?.message || 'An error occurred');
      setLoading(false);
    }
  };

  return (
    <FormWrapper>
      {error && (
        <Alert
          message={intl.formatMessage(PaymentMethodModalMessages.error)}
          description={error}
          type="error"
          showIcon
          style={{marginBottom: 24}}
        />
      )}
      <Form.Item label={intl.formatMessage(PaymentMethodModalMessages.cardNumber)}>
        <CardElement
          options={{
            style: {
              base: {
                fontSize: '16px',
                color: '#424770',
                '::placeholder': {
                  color: '#aab7c4',
                },
              },
              invalid: {
                color: '#9e2146',
              },
            },
          }}
        />
      </Form.Item>
      <div style={{display: 'flex', justifyContent: 'flex-end', columnGap: 8, marginTop: 24}}>
        <CustomButton type="button" onClick={onCancel} disabled={loading}>
          {intl.formatMessage(PaymentMethodModalMessages.cancel)}
        </CustomButton>
        <CustomButton
          type="primary"
          htmlType="button"
          loading={loading}
          disabled={loading || !stripe}
          onClick={() => handleSubmit()}
        >
          {intl.formatMessage(PaymentMethodModalMessages.submit)}
        </CustomButton>
      </div>
    </FormWrapper>
  );
};

// Get Stripe publishable key from environment
const getStripePublishableKey = (): string | null => {
  // Vite uses import.meta.env instead of process.env
  // With envPrefix: 'REACT_APP_' in vite.config.ts, variables are available as import.meta.env.REACT_APP_*
  const key = import.meta.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
  if (!key) {
    console.error('REACT_APP_STRIPE_PUBLISHABLE_KEY is not set');
    return null;
  }
  // Publishable key must start with pk_ (not sk_ which is the secret key)
  if (!key.startsWith('pk_')) {
    console.error('REACT_APP_STRIPE_PUBLISHABLE_KEY must start with pk_ (publishable key). You may have set the secret key (sk_) by mistake.');
    return null;
  }
  return key;
};

// In dev over HTTP, Stripe.js logs: "You may test your Stripe.js integration over HTTP..."
// We temporarily suppress that single warning so the console stays clean.
// Stripe.js logs "You may test... over HTTP... must use HTTPS" in dev. Filter it so the console stays clean.
const loadStripeWithQuietDevWarning = (key: string): Promise<any> => {
  const origWarn = console.warn;
  console.warn = (...args: unknown[]) => {
    const msg = typeof args[0] === 'string' ? args[0] : String(args[0] ?? '');
    if (msg.includes('Stripe') && msg.includes('HTTP') && msg.includes('HTTPS')) return;
    origWarn.apply(console, args);
  };
  const p = loadStripe(key);
  p.finally(() => setTimeout(() => { console.warn = origWarn; }, 1500));
  return p;
};

// Initialize Stripe promise lazily
let stripePromise: Promise<any> | null = null;
const getStripePromise = (): Promise<any> | null => {
  if (!stripePromise) {
    const key = getStripePublishableKey();
    if (key) {
      stripePromise = loadStripeWithQuietDevWarning(key);
    }
  }
  return stripePromise;
};

export const PaymentMethodModal: React.FC<IPaymentMethodModal> = ({isVisible, setIsVisible, onSuccess}) => {
  const intl = useIntl();
  const dispatch = useAppDispatch();
  
  const createSetupIntentState = useAppSelector(state => state.tariff.createSetupIntent);
  const attachPaymentMethodState = useAppSelector(state => state.tariff.attachPaymentMethod);
  const clientSecret = createSetupIntentState.clientSecret;

  // Create new setup intent when modal opens
  useEffect(() => {
    if (isVisible && !clientSecret && !createSetupIntentState.isLoading && !createSetupIntentState.errorMessage) {
      // Clear any previous setup intent first
      dispatch(clearCreateSetupIntent());
      // Create new setup intent
      dispatch(createSetupIntent({}));
    }
  }, [isVisible, clientSecret, createSetupIntentState.isLoading, createSetupIntentState.errorMessage, dispatch]);

  // Clear setup intent when modal closes to prevent reuse
  useEffect(() => {
    if (!isVisible) {
      dispatch(clearCreateSetupIntent());
    }
  }, [isVisible, dispatch]);

  useEffect(() => {
    if (attachPaymentMethodState.isSuccess) {
      message.success(intl.formatMessage(PaymentMethodModalMessages.success));
      dispatch(clearAttachPaymentMethod());
      if (onSuccess) {
        onSuccess();
      }
      setIsVisible(false);
    }
  }, [attachPaymentMethodState.isSuccess, dispatch, onSuccess, setIsVisible, intl]);

  const handleSubmit = (paymentMethodId: string) => {
    dispatch(attachPaymentMethod({paymentMethodId}));
  };

  const handleCancel = () => {
    dispatch(clearCreateSetupIntent());
    dispatch(clearAttachPaymentMethod());
    setIsVisible(false);
  };

  const isLoading = createSetupIntentState.isLoading || attachPaymentMethodState.isLoading;

  return (
    <CustomModal
      title={intl.formatMessage(PaymentMethodModalMessages.title)}
      open={isVisible}
      onCancel={handleCancel}
      footer={null}
      width={500}
    >
      <ModalContent>
        <div>{intl.formatMessage(PaymentMethodModalMessages.description)}</div>
        
        {createSetupIntentState.errorMessage && (
          <Alert
            message={intl.formatMessage(PaymentMethodModalMessages.error)}
            description={createSetupIntentState.errorMessage}
            type="error"
            showIcon
          />
        )}
        
        {attachPaymentMethodState.errorMessage && (
          <Alert
            message={intl.formatMessage(PaymentMethodModalMessages.error)}
            description={attachPaymentMethodState.errorMessage}
            type="error"
            showIcon
          />
        )}
        
        {isLoading && !clientSecret && (
          <div>Loading...</div>
        )}
        
        {clientSecret && (() => {
          const stripe = getStripePromise();
          if (!stripe) {
            return (
              <Alert
                message={intl.formatMessage(PaymentMethodModalMessages.error)}
                description="Stripe is not configured. Please contact support."
                type="error"
                showIcon
              />
            );
          }
          return (
            <Elements stripe={stripe}>
              <PaymentMethodForm
                clientSecret={clientSecret}
                onSubmit={handleSubmit}
                onCancel={handleCancel}
                attachLoading={attachPaymentMethodState.isLoading}
                attachError={attachPaymentMethodState.errorMessage || ''}
              />
            </Elements>
          );
        })()}
      </ModalContent>
    </CustomModal>
  );
};
